export type RfwEventType = 'presencial' | 'virtual' | 'transmision-especial' | 'concierto' | 'entrevista' | 'festival' | 'sorteo' | 'meet-greet';

export type RfwEventStatus = 'hoy' | 'proximamente' | 'finalizado';

export interface RfwEventCounter {
  views: number;
  attendees: number;
  interested: number;
  shared: number;
  comments: number;
}

export interface RfwEventScore {
  createEvent: number;
  participate: number;
  attend: number;
  share: number;
  invite: number;
}

export interface RfwEvent {
  id: string;
  title: string;
  description: string;
  type: RfwEventType;
  status: RfwEventStatus;
  date: string;
  time: string;
  stationName: string;
  stationFrequency: string;
  stationLogoUrl: string;
  imageUrl: string;
  city: string;
  state: string;
  country: string;
  location: string;
  modality: 'presencial' | 'online' | 'hibrido';
  capacity: number;
  price: string;
  clubId: string | null;
  clubName: string | null;
  isClubExclusive: boolean;
  counters: RfwEventCounter;
  score: RfwEventScore;
  tags: string[];
}

export const eventTypeLabels: Record<RfwEventType, string> = {
  presencial: 'Evento Presencial',
  virtual: 'Evento Virtual',
  'transmision-especial': 'Transmisión Especial',
  concierto: 'Concierto',
  entrevista: 'Entrevista',
  festival: 'Festival',
  sorteo: 'Sorteo',
  'meet-greet': 'Meet & Greet',
};

export const eventTypeIcons: Record<RfwEventType, string> = {
  presencial: 'ri-map-pin-line',
  virtual: 'ri-global-line',
  'transmision-especial': 'ri-broadcast-line',
  concierto: 'ri-music-2-line',
  entrevista: 'ri-mic-line',
  festival: 'ri-fire-line',
  sorteo: 'ri-gift-line',
  'meet-greet': 'ri-hand-heart-line',
};

export const eventTypeColors: Record<RfwEventType, string> = {
  presencial: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  virtual: 'bg-sky-50 text-sky-700 border-sky-200',
  'transmision-especial': 'bg-violet-50 text-violet-700 border-violet-200',
  concierto: 'bg-rose-50 text-rose-700 border-rose-200',
  entrevista: 'bg-amber-50 text-amber-700 border-amber-200',
  festival: 'bg-orange-50 text-orange-700 border-orange-200',
  sorteo: 'bg-teal-50 text-teal-700 border-teal-200',
  'meet-greet': 'bg-pink-50 text-pink-700 border-pink-200',
};

export const eventTypeBgLight: Record<RfwEventType, string> = {
  presencial: 'bg-emerald-50',
  virtual: 'bg-sky-50',
  'transmision-especial': 'bg-violet-50',
  concierto: 'bg-rose-50',
  entrevista: 'bg-amber-50',
  festival: 'bg-orange-50',
  sorteo: 'bg-teal-50',
  'meet-greet': 'bg-pink-50',
};

export const eventTypeTextColors: Record<RfwEventType, string> = {
  presencial: 'text-emerald-700',
  virtual: 'text-sky-700',
  'transmision-especial': 'text-violet-700',
  concierto: 'text-rose-700',
  entrevista: 'text-amber-700',
  festival: 'text-orange-700',
  sorteo: 'text-teal-700',
  'meet-greet': 'text-pink-700',
};

export const eventTypeDotColors: Record<RfwEventType, string> = {
  presencial: 'bg-emerald-500',
  virtual: 'bg-sky-500',
  'transmision-especial': 'bg-violet-500',
  concierto: 'bg-rose-500',
  entrevista: 'bg-amber-500',
  festival: 'bg-orange-500',
  sorteo: 'bg-teal-500',
  'meet-greet': 'bg-pink-500',
};

export const statusLabels: Record<RfwEventStatus, string> = {
  hoy: 'Hoy',
  proximamente: 'Próximamente',
  finalizado: 'Finalizado',
};

export const statusDots: Record<RfwEventStatus, string> = {
  hoy: 'bg-red-500',
  proximamente: 'bg-emerald-500',
  finalizado: 'bg-foreground-300',
};

export const statusBgColors: Record<RfwEventStatus, string> = {
  hoy: 'bg-red-50 text-red-700',
  proximamente: 'bg-emerald-50 text-emerald-700',
  finalizado: 'bg-foreground-100 text-foreground-500',
};

export const functionLabels: Record<string, string> = {
  attend: 'Asistiré',
  interest: 'Me Interesa',
  share: 'Compartir',
  invite: 'Invitar',
  comment: 'Comentar',
  save: 'Guardar',
};

export const functionIcons: Record<string, string> = {
  attend: 'ri-calendar-check-line',
  interest: 'ri-heart-line',
  share: 'ri-share-forward-line',
  invite: 'ri-user-add-line',
  comment: 'ri-chat-3-line',
  save: 'ri-bookmark-line',
};

export const functionActiveIcons: Record<string, string> = {
  attend: 'ri-calendar-check-fill',
  interest: 'ri-heart-fill',
  share: 'ri-share-forward-fill',
  invite: 'ri-user-add-fill',
  comment: 'ri-chat-3-fill',
  save: 'ri-bookmark-fill',
};

export const eventDashboardStats = {
  totalEvents: 286,
  activeNow: 8,
  todayEvents: 6,
  upcomingThisWeek: 42,
  totalAttendees: 184500,
  totalInterested: 312800,
  totalShared: 89200,
  totalComments: 156300,
  clubExclusiveEvents: 38,
  totalScoreAwarded: 2405000,
  stationsParticipating: 12,
};

export const rfwEvents: RfwEvent[] = [
  {
    id: 'evt-001',
    title: 'Noche de Karaoke con los Locutores',
    description: 'Ven a cantar con nosotros. Noche especial de karaoke donde tú eres la estrella. Premios sorpresa para los mejores cantantes, bebidas 2x1 y transmisión en vivo desde el bar.',
    type: 'presencial',
    status: 'hoy',
    date: '2026-06-21',
    time: '19:00 - 23:00',
    stationName: 'Radio Nueva Actitud',
    stationFrequency: '94.70 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20icon%20logo%20design%2C%20circular%20bright%20pop%20music%20theme%2C%20clean%20minimal%20style%2C%20coral%20and%20warm%20tones%2C%20professional%20broadcast%20identity&width=100&height=100&seq=logo-nueva-actitud&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Karaoke%20night%20at%20lively%20bar%20venue%2C%20neon%20lights%20and%20stage%20with%20microphone%20stand%2C%20people%20singing%20and%20having%20fun%2C%20colorful%20disco%20lighting%2C%20entertainment%20atmosphere%2C%20warm%20social%20gathering%2C%20candid%20bar%20photography%20with%20vibrant%20energy&width=800&height=500&seq=rfwev-karaoke-bar-001&orientation=landscape',
    city: 'Santa Rosa',
    state: 'California',
    country: 'Estados Unidos',
    location: 'Bar La Esquina, Calle 7 #234, Santa Rosa, CA 95403',
    modality: 'presencial',
    capacity: 200,
    price: 'Gratuito',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 8700, attendees: 145, interested: 280, shared: 320, comments: 189 },
    score: { createEvent: 500, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['música', 'entretenimiento', 'social'],
  },
  {
    id: 'evt-002',
    title: 'Concierto de Alabanza: Noche de Adoración',
    description: 'Noche especial de alabanza y adoración con el Coro Celestial y artistas invitados. Entrada gratuita. Transmisión en vivo por 87.00 RFW.',
    type: 'concierto',
    status: 'hoy',
    date: '2026-06-21',
    time: '19:00 - 22:00',
    stationName: 'Radio Fe y Esperanza',
    stationFrequency: '87.00 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Faith%20based%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20dove%20and%20cross%20symbolism%2C%20warm%20golden%20tones%2C%20clean%20modern%20religious%20brand%20identity&width=100&height=100&seq=logo-fe-esperanza&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Gospel%20worship%20concert%20at%20large%20church%20auditorium%2C%20choir%20singing%20passionately%20on%20stage%2C%20warm%20golden%20lighting%2C%20congregation%20with%20raised%20hands%2C%20spiritual%20uplifting%20atmosphere&width=800&height=500&seq=rfwev-worship-concert-002&orientation=landscape',
    city: 'Miami',
    state: 'Florida',
    country: 'Estados Unidos',
    location: 'Iglesia Comunidad de Fe, 8900 SW 168th St, Palmetto Bay, FL 33157',
    modality: 'presencial',
    capacity: 800,
    price: 'Gratuito',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 7620, attendees: 620, interested: 910, shared: 540, comments: 312 },
    score: { createEvent: 500, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['religioso', 'música', 'comunidad'],
  },
  {
    id: 'evt-003',
    title: 'Noche de Mariachi en Vivo',
    description: 'Celebra con nosotros una noche dedicada al mariachi. Presentaciones del Mariachi Sol de México, cena buffet, barra libre de margaritas y transmisión especial.',
    type: 'concierto',
    status: 'hoy',
    date: '2026-06-21',
    time: '18:00 - 22:00',
    stationName: 'La Mexicana FM',
    stationFrequency: '103.50 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20vibrant%20green%20red%20and%20white%20colors%2C%20traditional%20Mexican%20aesthetic%2C%20bold%20festive%20brand%20identity&width=100&height=100&seq=logo-mexicana-fm&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Mariachi%20band%20performing%20live%20at%20elegant%20Mexican%20event%20hall%2C%20musicians%20in%20traditional%20charro%20suits%2C%20warm%20festive%20lighting%2C%20audience%20enjoying%20cultural%20performance%2C%20decorative%20papel%20picado%20banners&width=800&height=500&seq=rfwev-mariachi-live-003&orientation=landscape',
    city: 'Houston',
    state: 'Texas',
    country: 'Estados Unidos',
    location: 'Salón Los Ángeles, 4520 Hillcroft Ave, Houston, TX 77036',
    modality: 'presencial',
    capacity: 500,
    price: '$25 USD',
    clubId: 'club-003',
    clubName: 'Club Oficial La Mexicana FM',
    isClubExclusive: true,
    counters: { views: 9100, attendees: 380, interested: 1200, shared: 890, comments: 520 },
    score: { createEvent: 500, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['cultural', 'música', 'club', 'exclusivo'],
  },
  {
    id: 'evt-004',
    title: 'Batalla de Bandas: Edición Verano',
    description: '10 bandas emergentes compiten por el título de Mejor Banda del Verano. Premio: grabación de EP profesional y rotación en Rock Total por 3 meses.',
    type: 'festival',
    status: 'hoy',
    date: '2026-06-21',
    time: '16:00 - 23:00',
    stationName: 'Rock Total Radio',
    stationFrequency: '87.01 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20electric%20guitar%20and%20skull%20motifs%2C%20dark%20edgy%20aesthetic%2C%20bold%20metallic%20tones&width=100&height=100&seq=logo-rock-total&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Rock%20band%20battle%20competition%20at%20gritty%20underground%20venue%2C%20musicians%20performing%20intensely%20on%20stage%20with%20electric%20guitars%2C%20dramatic%20red%20and%20blue%20stage%20lighting%2C%20energetic%20crowd%20with%20hands%20raised&width=800&height=500&seq=rfwev-band-battle-004&orientation=landscape',
    city: 'Los Ángeles',
    state: 'California',
    country: 'Estados Unidos',
    location: 'The Garage Venue, 4520 Sunset Blvd, Los Angeles, CA 90027',
    modality: 'presencial',
    capacity: 400,
    price: '$10 USD',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 11800, attendees: 360, interested: 1800, shared: 1200, comments: 890 },
    score: { createEvent: 500, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['música', 'competencia', 'rock'],
  },
  {
    id: 'evt-005',
    title: 'Cobertura en Vivo: Final de Conferencia',
    description: 'Transmisión especial desde el estadio con todo el análisis pre y post partido. Fernando Gutiérrez y La Mesa Redonda en vivo.',
    type: 'transmision-especial',
    status: 'hoy',
    date: '2026-06-21',
    time: '17:00 - 22:00',
    stationName: 'Deportes Total Radio',
    stationFrequency: '120.00 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20football%20and%20star%20motifs%2C%20bold%20energetic%20colors%2C%20dynamic%20sports%20brand%20identity&width=100&height=100&seq=logo-deportes-total&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Sports%20stadium%20exterior%20with%20bright%20lights%20and%20massive%20crowd%20entering%20for%20championship%20game%2C%20dramatic%20evening%20sky%2C%20broadcast%20trucks%20and%20media%20setup%20visible%2C%20large%20scale%20event%20photography&width=800&height=500&seq=rfwev-stadium-broadcast-005&orientation=landscape',
    city: 'Dallas',
    state: 'Texas',
    country: 'Estados Unidos',
    location: 'Estadio AT&T, 1 AT&T Way, Arlington, TX 76011',
    modality: 'presencial',
    capacity: 80000,
    price: 'Desde $45 USD',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 18900, attendees: 45000, interested: 92000, shared: 6700, comments: 3400 },
    score: { createEvent: 800, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['deportes', 'transmisión', 'futbol'],
  },
  {
    id: 'evt-006',
    title: 'Entrevista Exclusiva: Leyendas del Rock',
    description: 'Conversación íntima con tres leyendas del rock latinoamericano. Conocerás sus historias nunca antes contadas, anécdotas de gira y qué están preparando. Solo para miembros del club.',
    type: 'entrevista',
    status: 'hoy',
    date: '2026-06-21',
    time: '20:00 - 21:30',
    stationName: 'Rock Total Radio',
    stationFrequency: '87.01 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20electric%20guitar%20and%20skull%20motifs%2C%20dark%20edgy%20aesthetic%2C%20bold%20metallic%20tones&width=100&height=100&seq=logo-rock-total&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Intimate%20interview%20setting%20with%20rock%20musician%20in%20dimly%20lit%20studio%2C%20microphone%20and%20acoustic%20guitar%20in%20foreground%2C%20warm%20amber%20lighting%2C%20storytelling%20atmosphere%2C%20documentary%20style%20portrait%20photography&width=800&height=500&seq=rfwev-rock-interview-006&orientation=landscape',
    city: 'Los Ángeles',
    state: 'California',
    country: 'Estados Unidos',
    location: 'Online - Zoom Exclusivo',
    modality: 'online',
    capacity: 100,
    price: 'Miembros Club',
    clubId: 'club-005',
    clubName: 'Club Oficial Rock Total Radio',
    isClubExclusive: true,
    counters: { views: 3200, attendees: 78, interested: 450, shared: 190, comments: 230 },
    score: { createEvent: 300, participate: 25, attend: 75, share: 15, invite: 20 },
    tags: ['música', 'entrevista', 'club', 'exclusivo'],
  },
  {
    id: 'evt-007',
    title: 'Festival de Música en el Parque Central',
    description: 'Acompáñanos en el festival anual de música del Parque Central. 8 bandas locales, food trucks, zona de juegos para niños y transmisión especial desde el escenario principal. Entrada gratuita.',
    type: 'festival',
    status: 'proximamente',
    date: '2026-07-14',
    time: '14:00 - 22:00',
    stationName: 'Radio Nueva Actitud',
    stationFrequency: '94.70 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20icon%20logo%20design%2C%20circular%20bright%20pop%20music%20theme%2C%20clean%20minimal%20style%2C%20coral%20and%20warm%20tones%2C%20professional%20broadcast%20identity&width=100&height=100&seq=logo-nueva-actitud&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Outdoor%20summer%20music%20festival%20event%20in%20park%2C%20colorful%20vibrant%20stage%20with%20lights%20and%20sound%20equipment%2C%20crowd%20of%20people%20dancing%20and%20enjoying%20live%20music%2C%20food%20trucks%20and%20festival%20tents%2C%20California%20sunny%20day%20atmosphere%2C%20palm%20trees&width=800&height=500&seq=rfwev-park-festival-007&orientation=landscape',
    city: 'Santa Rosa',
    state: 'California',
    country: 'Estados Unidos',
    location: 'Parque Central, Av. Principal 450, Santa Rosa, CA 95401',
    modality: 'presencial',
    capacity: 5000,
    price: 'Gratuito',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 4520, attendees: 0, interested: 3400, shared: 2100, comments: 890 },
    score: { createEvent: 500, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['música', 'festival', 'familiar'],
  },
  {
    id: 'evt-008',
    title: 'Tributo a Metallica: Master of Puppets',
    description: 'Noche especial de tributo a Metallica. 5 bandas tributo tocando el disco completo más los clásicos. Edición limitada de playeras conmemorativas.',
    type: 'concierto',
    status: 'proximamente',
    date: '2026-07-12',
    time: '20:00 - 01:00',
    stationName: 'Rock Total Radio',
    stationFrequency: '87.01 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20electric%20guitar%20and%20skull%20motifs%2C%20dark%20edgy%20aesthetic%2C%20bold%20metallic%20tones&width=100&height=100&seq=logo-rock-total&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Metallica%20tribute%20concert%20at%20iconic%20rock%20venue%2C%20band%20performing%20with%20intense%20energy%20on%20dimly%20lit%20stage%2C%20dramatic%20blue%20and%20white%20spotlighting%2C%20packed%20crowd%20with%20devil%20horns%20hand%20signs&width=800&height=500&seq=rfwev-metallica-tribute-008&orientation=landscape',
    city: 'Los Ángeles',
    state: 'California',
    country: 'Estados Unidos',
    location: 'The Roxy Theatre, 9009 Sunset Blvd, West Hollywood, CA 90069',
    modality: 'presencial',
    capacity: 500,
    price: '$35 USD',
    clubId: 'club-005',
    clubName: 'Club Oficial Rock Total Radio',
    isClubExclusive: true,
    counters: { views: 20100, attendees: 0, interested: 3200, shared: 4500, comments: 2100 },
    score: { createEvent: 500, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['música', 'concierto', 'rock', 'club', 'exclusivo'],
  },
  {
    id: 'evt-009',
    title: 'Fiesta del Grito: Celebración Patria',
    description: 'La fiesta mexicana más grande de Houston. Música en vivo con banda y norteño, antojitos mexicanos, juegos mecánicos, show de fuegos artificiales y ceremonia del Grito a las 11 PM.',
    type: 'festival',
    status: 'proximamente',
    date: '2026-09-15',
    time: '16:00 - 02:00',
    stationName: 'La Mexicana FM',
    stationFrequency: '103.50 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20vibrant%20green%20red%20and%20white%20colors%2C%20traditional%20Mexican%20aesthetic%2C%20bold%20festive%20brand%20identity&width=100&height=100&seq=logo-mexicana-fm&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Mexican%20Independence%20Day%20outdoor%20fiesta%20celebration%2C%20massive%20crowd%20waving%20flags%2C%20colorful%20stage%20with%20banda%20musicians%20performing%2C%20festive%20lights%20and%20fireworks%2C%20traditional%20Mexican%20atmosphere&width=800&height=500&seq=rfwev-grito-festival-009&orientation=landscape',
    city: 'Houston',
    state: 'Texas',
    country: 'Estados Unidos',
    location: 'Plaza México, 6000 Richmond Ave, Houston, TX 77057',
    modality: 'presencial',
    capacity: 10000,
    price: 'Gratuito',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 15700, attendees: 0, interested: 8200, shared: 6100, comments: 2800 },
    score: { createEvent: 800, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['cultural', 'festival', 'comunidad'],
  },
  {
    id: 'evt-010',
    title: 'Sorteo: Boletos para el Super Bowl',
    description: 'Participa en el sorteo de un par de boletos para el Super Bowl con viaje y hospedaje incluidos. Solo tenés que estar registrado y seguir a Deportes Total Radio.',
    type: 'sorteo',
    status: 'proximamente',
    date: '2026-07-01',
    time: '00:00 - 23:59',
    stationName: 'Deportes Total Radio',
    stationFrequency: '120.00 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20football%20and%20star%20motifs%2C%20bold%20energetic%20colors%2C%20dynamic%20sports%20brand%20identity&width=100&height=100&seq=logo-deportes-total&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Super%20Bowl%20tickets%20giveaway%20concept%2C%20golden%20tickets%20floating%20with%20confetti%2C%20football%20stadium%20in%20background%2C%20exciting%20prize%20atmosphere%2C%20sports%20celebration%20photography%20with%20dramatic%20lighting&width=800&height=500&seq=rfwev-superbowl-sorteo-010&orientation=landscape',
    city: 'Online',
    state: 'Global',
    country: 'Estados Unidos',
    location: 'Online - Web y App RFW',
    modality: 'online',
    capacity: 99999,
    price: 'Gratuito',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 45600, attendees: 0, interested: 38500, shared: 18200, comments: 7600 },
    score: { createEvent: 800, participate: 50, attend: 0, share: 25, invite: 35 },
    tags: ['deportes', 'sorteo', 'premio'],
  },
  {
    id: 'evt-011',
    title: 'Meet & Greet con Ricky Navarro',
    description: 'Conocé en persona al locutor #1 de RFW. Sesión de fotos, firma de autógrafos y charla íntima con Ricky. Cupo limitadísimo para miembros del Club VIP.',
    type: 'meet-greet',
    status: 'proximamente',
    date: '2026-07-20',
    time: '17:00 - 20:00',
    stationName: 'Banda Radio MX',
    stationFrequency: '200.00 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Banda%20music%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20brass%20instruments%20and%20Mexican%20motifs%2C%20bold%20red%20white%20and%20green%20colors%2C%20festive%20regional%20brand%20identity&width=100&height=100&seq=logo-banda-mx&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Radio%20host%20meet%20and%20greet%20event%20at%20studio%20lounge%2C%20charismatic%20host%20interacting%20with%20fans%2C%20warm%20welcoming%20atmosphere%2C%20autograph%20signing%2C%20professional%20studio%20environment%20with%20branded%20backdrop&width=800&height=500&seq=rfwev-meet-greet-ricky-011&orientation=landscape',
    city: 'San Antonio',
    state: 'Texas',
    country: 'Estados Unidos',
    location: 'Estudios Banda Radio MX, 3201 E Houston St, San Antonio, TX 78219',
    modality: 'presencial',
    capacity: 50,
    price: 'Miembros VIP',
    clubId: 'club-012',
    clubName: 'Club VIP Banda Radio MX',
    isClubExclusive: true,
    counters: { views: 8900, attendees: 0, interested: 2100, shared: 980, comments: 670 },
    score: { createEvent: 500, participate: 25, attend: 100, share: 15, invite: 20 },
    tags: ['social', 'club', 'exclusivo', 'locutor'],
  },
  {
    id: 'evt-012',
    title: 'Transmisión Especial: 24 Horas de Música Continua',
    description: 'Maratón de 24 horas de música sin interrupciones. Cada hora un género distinto. Invitados sorpresa, entrevistas relámpago y los mejores momentos del año.',
    type: 'transmision-especial',
    status: 'proximamente',
    date: '2026-07-25',
    time: '00:00 - 23:59',
    stationName: 'Radio Internacional Voz',
    stationFrequency: '140.00 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=International%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20globe%20and%20sound%20waves%2C%20cosmopolitan%20blue%20and%20gold%20tones%2C%20global%20broadcast%20brand%20identity&width=100&height=100&seq=logo-internacional&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Radio%20broadcast%20marathon%20studio%20setup%2C%20warm%20glowing%20on-air%20sign%2C%20professional%20mixing%20console%20with%20colorful%20buttons%2C%20headphones%20resting%20on%20desk%2C%20creative%20broadcast%20atmosphere%2C%20late%20night%20radio%20vibes%20photography&width=800&height=500&seq=rfwev-24hr-marathon-012&orientation=landscape',
    city: 'Online',
    state: 'Global',
    country: 'Estados Unidos',
    location: 'Online - 140.00 RFW',
    modality: 'online',
    capacity: 999999,
    price: 'Gratuito',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 6800, attendees: 0, interested: 5100, shared: 2300, comments: 1100 },
    score: { createEvent: 500, participate: 25, attend: 25, share: 15, invite: 10 },
    tags: ['música', 'transmisión', 'especial'],
  },
  {
    id: 'evt-013',
    title: 'Concierto Virtual: Voces del Mundo',
    description: '8 artistas de 8 países distintos en un concierto virtual único. Desde tu casa, conectate y disfrutá de la diversidad musical global. Interactuá en el chat en vivo.',
    type: 'concierto',
    status: 'proximamente',
    date: '2026-08-05',
    time: '18:00 - 22:00',
    stationName: 'Radio Internacional Voz',
    stationFrequency: '140.00 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=International%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20globe%20and%20sound%20waves%2C%20cosmopolitan%20blue%20and%20gold%20tones%2C%20global%20broadcast%20brand%20identity&width=100&height=100&seq=logo-internacional&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Virtual%20concert%20streaming%20setup%20with%20multiple%20artist%20screens%20on%20large%20display%2C%20professional%20broadcasting%20equipment%2C%20world%20map%20backdrop%2C%20colorful%20stage%20lighting%2C%20global%20music%20event%20production%20photography&width=800&height=500&seq=rfwev-virtual-concert-013&orientation=landscape',
    city: 'Online',
    state: 'Global',
    country: 'Estados Unidos',
    location: 'Online - Plataforma RFW',
    modality: 'online',
    capacity: 50000,
    price: '$5 USD',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 12300, attendees: 0, interested: 8700, shared: 4200, comments: 1900 },
    score: { createEvent: 800, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['música', 'virtual', 'internacional'],
  },
  {
    id: 'evt-014',
    title: 'Entrevista: El Futuro de la Radio Digital',
    description: 'Panel de expertos discutiendo hacia dónde va la radio. Directores de programación, locutores legendarios y visionarios tech comparten sus predicciones.',
    type: 'entrevista',
    status: 'proximamente',
    date: '2026-07-08',
    time: '10:00 - 12:00',
    stationName: 'Noticias 24/7 Radio',
    stationFrequency: '107.90 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=News%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20globe%20and%20microphone%20motifs%2C%20professional%20tones%2C%20authoritative%20news%20brand%20identity%2C%20clean%20modern%20aesthetic&width=100&height=100&seq=logo-noticias-247&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20panel%20discussion%20about%20digital%20media%20future%2C%20experts%20at%20conference%20table%20with%20microphones%2C%20modern%20broadcast%20studio%20setting%2C%20large%20screens%20showing%20data%2C%20intellectual%20atmosphere%20photography&width=800&height=500&seq=rfwev-radio-future-panel-014&orientation=landscape',
    city: 'Nueva York',
    state: 'Nueva York',
    country: 'Estados Unidos',
    location: 'Columbia University, 116th St & Broadway, New York, NY 10027',
    modality: 'hibrido',
    capacity: 300,
    price: 'Gratuito',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 4100, attendees: 0, interested: 2200, shared: 890, comments: 450 },
    score: { createEvent: 300, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['educativo', 'entrevista', 'radio', 'tecnología'],
  },
  {
    id: 'evt-015',
    title: 'Sorteo Exclusivo Club: Viaje a Cancún',
    description: 'Sorteo exclusivo para miembros del Club Oficial de La Mexicana FM. Ganá un viaje todo pagado a Cancún para 2 personas. 5 días, 4 noches en resort todo incluido.',
    type: 'sorteo',
    status: 'proximamente',
    date: '2026-08-15',
    time: '12:00 - 12:00',
    stationName: 'La Mexicana FM',
    stationFrequency: '103.50 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20vibrant%20green%20red%20and%20white%20colors%2C%20traditional%20Mexican%20aesthetic%2C%20bold%20festive%20brand%20identity&width=100&height=100&seq=logo-mexicana-fm&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Cancun%20tropical%20beach%20resort%20giveaway%20concept%2C%20golden%20envelope%20with%20plane%20tickets%20on%20pristine%20white%20sand%2C%20turquoise%20ocean%20background%2C%20luxury%20vacation%20prize%20atmosphere%2C%20travel%20photography&width=800&height=500&seq=rfwev-cancun-sorteo-015&orientation=landscape',
    city: 'Online',
    state: 'Club',
    country: 'Estados Unidos',
    location: 'Online - Solo Miembros del Club',
    modality: 'online',
    capacity: 9999,
    price: 'Miembros Club',
    clubId: 'club-003',
    clubName: 'Club Oficial La Mexicana FM',
    isClubExclusive: true,
    counters: { views: 6700, attendees: 0, interested: 4100, shared: 1500, comments: 980 },
    score: { createEvent: 500, participate: 50, attend: 0, share: 25, invite: 35 },
    tags: ['sorteo', 'club', 'exclusivo', 'viaje'],
  },
  {
    id: 'evt-016',
    title: 'Meet & Greet Virtual con Locutores',
    description: 'Sesión virtual de preguntas y respuestas con los 5 locutores más votados del mes. Preguntales lo que quieras, conocelos en persona (virtual) y participá por premios.',
    type: 'meet-greet',
    status: 'proximamente',
    date: '2026-07-28',
    time: '19:00 - 21:00',
    stationName: 'Radio Voz del Pueblo',
    stationFrequency: '160.00 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Community%20grassroots%20radio%20logo%20design%2C%20circular%20emblem%20with%20microphone%20and%20raised%20fist%2C%20warm%20earthy%20tones%2C%20activist%20brand%20identity&width=100&height=100&seq=logo-voz-pueblo&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Virtual%20video%20call%20grid%20with%20multiple%20radio%20hosts%20on%20screen%2C%20friendly%20engaging%20atmosphere%2C%20casual%20studio%20backgrounds%2C%20community%20interaction%2C%20warm%20modern%20video%20conferencing%20photography&width=800&height=500&seq=rfwev-virtual-meet-greet-016&orientation=landscape',
    city: 'Online',
    state: 'Colorado',
    country: 'Estados Unidos',
    location: 'Online - Zoom RFW',
    modality: 'online',
    capacity: 500,
    price: 'Gratuito',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 3400, attendees: 0, interested: 2800, shared: 700, comments: 520 },
    score: { createEvent: 300, participate: 25, attend: 50, share: 15, invite: 20 },
    tags: ['social', 'locutor', 'virtual'],
  },
  {
    id: 'evt-017',
    title: 'Taller de Radio para Jóvenes',
    description: 'Aprende cómo se hace radio. Taller gratuito para jóvenes de 14 a 21 años. Conoce el equipo, practica locución y descubre cómo producir tu propio programa.',
    type: 'presencial',
    status: 'proximamente',
    date: '2026-07-05',
    time: '09:00 - 14:00',
    stationName: 'Radio Nueva Actitud',
    stationFrequency: '94.70 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20icon%20logo%20design%2C%20circular%20bright%20pop%20music%20theme%2C%20clean%20minimal%20style%2C%20coral%20and%20warm%20tones%2C%20professional%20broadcast%20identity&width=100&height=100&seq=logo-nueva-actitud&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Radio%20production%20workshop%20for%20teenagers%2C%20young%20students%20learning%20at%20broadcast%20studio%20with%20professional%20equipment%2C%20instructor%20demonstrating%20mixing%20console%2C%20educational%20creative%20atmosphere&width=800&height=500&seq=rfwev-workshop-studio-017&orientation=landscape',
    city: 'Santa Rosa',
    state: 'California',
    country: 'Estados Unidos',
    location: 'Estudios Nueva Actitud, Av. Tecnología 567, Santa Rosa, CA 95403',
    modality: 'presencial',
    capacity: 30,
    price: 'Gratuito',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 1890, attendees: 0, interested: 120, shared: 230, comments: 87 },
    score: { createEvent: 300, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['educativo', 'taller', 'radio'],
  },
  {
    id: 'evt-018',
    title: 'Baile Masivo de Banda',
    description: 'El baile de banda más grande del año. 5 agrupaciones en vivo, pista de baile gigante, zona VIP y transmisión especial por 200.00 RFW.',
    type: 'concierto',
    status: 'proximamente',
    date: '2026-06-29',
    time: '20:00 - 03:00',
    stationName: 'Banda Radio MX',
    stationFrequency: '200.00 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Banda%20music%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20brass%20instruments%20and%20Mexican%20motifs%2C%20bold%20red%20white%20and%20green%20colors%2C%20festive%20regional%20brand%20identity&width=100&height=100&seq=logo-banda-mx&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Massive%20Mexican%20banda%20music%20dance%20event%20at%20large%20coliseum%2C%20thousands%20of%20people%20dancing%2C%20live%20banda%20group%20on%20stage%20with%20brass%20instruments%2C%20colorful%20concert%20lighting%2C%20energetic%20celebration%20atmosphere&width=800&height=500&seq=rfwev-baile-banda-018&orientation=landscape',
    city: 'San Antonio',
    state: 'Texas',
    country: 'Estados Unidos',
    location: 'Freeman Coliseum, 3201 E Houston St, San Antonio, TX 78219',
    modality: 'presencial',
    capacity: 6000,
    price: '$30 USD',
    clubId: 'club-012',
    clubName: 'Club VIP Banda Radio MX',
    isClubExclusive: true,
    counters: { views: 19200, attendees: 0, interested: 8900, shared: 5400, comments: 2800 },
    score: { createEvent: 800, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['música', 'concierto', 'club', 'banda'],
  },
  {
    id: 'evt-019',
    title: 'Foro: Libertad de Expresión en la Radio',
    description: 'Foro abierto sobre la libertad de expresión en medios comunitarios. Panelistas internacionales, debates, networking y transmisión en vivo.',
    type: 'virtual',
    status: 'proximamente',
    date: '2026-06-28',
    time: '10:00 - 14:00',
    stationName: 'Noticias 24/7 Radio',
    stationFrequency: '107.90 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=News%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20globe%20and%20microphone%20motifs%2C%20professional%20tones%2C%20authoritative%20news%20brand%20identity%2C%20clean%20modern%20aesthetic&width=100&height=100&seq=logo-noticias-247&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Academic%20forum%20panel%20discussion%20at%20university%20auditorium%2C%20speakers%20at%20long%20table%20with%20microphones%2C%20audience%20of%20students%20and%20professionals%2C%20professional%20conference%20atmosphere%20photography&width=800&height=500&seq=rfwev-forum-panel-019&orientation=landscape',
    city: 'Nueva York',
    state: 'Nueva York',
    country: 'Estados Unidos',
    location: 'Columbia University + Online',
    modality: 'hibrido',
    capacity: 300,
    price: 'Gratuito',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 5300, attendees: 0, interested: 1800, shared: 940, comments: 390 },
    score: { createEvent: 300, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['educativo', 'foro', 'comunidad'],
  },
  {
    id: 'evt-020',
    title: 'Block Party: Fiesta de la Calle',
    description: 'La fiesta urbana más grande de Phoenix. DJs en vivo, batallas de breakdance, grafiti en vivo, food trucks y mercado de arte local. Entrada gratuita.',
    type: 'festival',
    status: 'proximamente',
    date: '2026-07-22',
    time: '15:00 - 01:00',
    stationName: 'La Kalle 99.9',
    stationFrequency: '99.90 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Urban%20pop%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20energetic%20graffiti%20style%2C%20vibrant%20neon%20colors%2C%20street%20culture%20brand%20identity%2C%20bold%20youthful%20aesthetic&width=100&height=100&seq=logo-la-kalle&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Urban%20block%20party%20street%20festival%2C%20DJ%20performing%20on%20outdoor%20stage%2C%20people%20dancing%20on%20closed%20street%2C%20colorful%20murals%20and%20street%20art%20background%2C%20food%20trucks%20and%20vendors%2C%20energetic%20summer%20night%20atmosphere&width=800&height=500&seq=rfwev-block-party-020&orientation=landscape',
    city: 'Phoenix',
    state: 'Arizona',
    country: 'Estados Unidos',
    location: 'Roosevelt Row, 900 N 1st St, Phoenix, AZ 85004',
    modality: 'presencial',
    capacity: 3000,
    price: 'Gratuito',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 14100, attendees: 0, interested: 6800, shared: 4100, comments: 1900 },
    score: { createEvent: 500, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['música', 'festival', 'urbano'],
  },
  {
    id: 'evt-021',
    title: 'Concierto Acústico: Sesión Íntima',
    description: 'Una noche mágica de música acústica con artistas locales. Formato íntimo con capacidad limitada a 200 personas. Transmitido en vivo por 94.70 RFW.',
    type: 'concierto',
    status: 'finalizado',
    date: '2026-06-01',
    time: '20:00 - 22:30',
    stationName: 'Radio Nueva Actitud',
    stationFrequency: '94.70 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20icon%20logo%20design%2C%20circular%20bright%20pop%20music%20theme%2C%20clean%20minimal%20style%2C%20coral%20and%20warm%20tones%2C%20professional%20broadcast%20identity&width=100&height=100&seq=logo-nueva-actitud&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Intimate%20acoustic%20music%20concert%20at%20small%20theater%2C%20singer%20with%20guitar%20on%20dimly%20lit%20stage%2C%20warm%20atmospheric%20spotlight%2C%20audience%20sitting%20in%20cozy%20venue%2C%20cultural%20artistic%20performance%20photography&width=800&height=500&seq=rfwev-acoustic-intimo-021&orientation=landscape',
    city: 'Santa Rosa',
    state: 'California',
    country: 'Estados Unidos',
    location: 'Teatro Municipal, Calle Cultura 123, Santa Rosa, CA 95404',
    modality: 'presencial',
    capacity: 200,
    price: 'Gratuito',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 12300, attendees: 198, interested: 450, shared: 890, comments: 340 },
    score: { createEvent: 500, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['música', 'concierto', 'íntimo'],
  },
  {
    id: 'evt-022',
    title: 'Rock en el Parque: Festival Gratuito',
    description: 'Festival gratuito de rock al aire libre. 15 bandas en 2 escenarios, zona de food trucks, mercado de vinilos y mercancía de bandas locales. Asistencia récord.',
    type: 'festival',
    status: 'finalizado',
    date: '2026-05-15',
    time: '12:00 - 20:00',
    stationName: 'Rock Total Radio',
    stationFrequency: '87.01 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20electric%20guitar%20and%20skull%20motifs%2C%20dark%20edgy%20aesthetic%2C%20bold%20metallic%20tones&width=100&height=100&seq=logo-rock-total&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Outdoor%20rock%20music%20festival%20in%20large%20park%20setting%2C%20multiple%20stages%20with%20bands%20performing%2C%20thousands%20of%20people%20on%20grassy%20field%2C%20sunny%20California%20day%2C%20food%20trucks%20and%20vendor%20tents%2C%20wide%20angle%20crowd%20shot&width=800&height=500&seq=rfwev-rock-park-022&orientation=landscape',
    city: 'Los Ángeles',
    state: 'California',
    country: 'Estados Unidos',
    location: 'Griffith Park, 4730 Crystal Springs Dr, Los Angeles, CA 90027',
    modality: 'presencial',
    capacity: 8000,
    price: 'Gratuito',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 25600, attendees: 7800, interested: 12000, shared: 8900, comments: 4200 },
    score: { createEvent: 800, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['música', 'festival', 'rock'],
  },
  {
    id: 'evt-023',
    title: 'Encuentro con Leyendas del Deporte',
    description: 'Noche de gala con ex estrellas del futbol mexicano y de la NFL. Panel de discusión, sesión de autógrafos, cena incluida y rifa de artículos deportivos firmados.',
    type: 'presencial',
    status: 'finalizado',
    date: '2026-06-03',
    time: '18:30 - 21:00',
    stationName: 'Deportes Total Radio',
    stationFrequency: '120.00 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20football%20and%20star%20motifs%2C%20bold%20energetic%20colors%2C%20dynamic%20sports%20brand%20identity&width=100&height=100&seq=logo-deportes-total&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Sports%20legends%20panel%20discussion%20at%20convention%20center%2C%20famous%20retired%20athletes%20on%20stage%20answering%20questions%2C%20large%20audience%20in%20auditorium%2C%20professional%20event%20photography&width=800&height=500&seq=rfwev-legends-panel-023&orientation=landscape',
    city: 'Dallas',
    state: 'Texas',
    country: 'Estados Unidos',
    location: 'Centro de Convenciones Dallas, 650 S Griffin St, Dallas, TX 75202',
    modality: 'presencial',
    capacity: 3000,
    price: '$20 USD',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 16700, attendees: 2800, interested: 5200, shared: 3200, comments: 1800 },
    score: { createEvent: 500, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['deportes', 'entrevista', 'leyendas'],
  },
  {
    id: 'evt-024',
    title: 'Feria Multicultural de Chicago',
    description: 'Celebración de la diversidad cultural de Chicago. Música del mundo, danzas tradicionales, gastronomía internacional y stands de organizaciones comunitarias.',
    type: 'festival',
    status: 'proximamente',
    date: '2026-07-25',
    time: '11:00 - 19:00',
    stationName: 'Radio Comunitaria Unida',
    stationFrequency: '87.02 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Community%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20people%20and%20heart%20motifs%2C%20warm%20earthy%20colors%2C%20inclusive%20grassroots%20brand%20identity&width=100&height=100&seq=logo-comunitaria&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Multicultural%20outdoor%20festival%20at%20city%20park%2C%20diverse%20performers%20on%20stage%20in%20traditional%20costumes%2C%20food%20vendors%20from%20various%20cultures%2C%20families%20enjoying%20summer%20day%2C%20colorful%20decorations%20and%20flags&width=800&height=500&seq=rfwev-multicultural-fair-024&orientation=landscape',
    city: 'Chicago',
    state: 'Illinois',
    country: 'Estados Unidos',
    location: 'Millennium Park, 201 E Randolph St, Chicago, IL 60602',
    modality: 'presencial',
    capacity: 5000,
    price: 'Gratuito',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 4100, attendees: 0, interested: 2800, shared: 1200, comments: 560 },
    score: { createEvent: 500, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['cultural', 'festival', 'comunidad'],
  },
  {
    id: 'evt-025',
    title: 'Sorteo Relámpago: Auriculares Profesionales',
    description: 'Participá y ganá auriculares profesionales para que escuches RFW con la mejor calidad. Sorteo cada hora durante 12 horas. Solo por hoy.',
    type: 'sorteo',
    status: 'hoy',
    date: '2026-06-21',
    time: '08:00 - 20:00',
    stationName: 'La Kalle 99.9',
    stationFrequency: '99.90 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Urban%20pop%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20energetic%20graffiti%20style%2C%20vibrant%20neon%20colors%2C%20street%20culture%20brand%20identity%2C%20bold%20youthful%20aesthetic&width=100&height=100&seq=logo-la-kalle&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20studio%20headphones%20on%20colorful%20gradient%20background%2C%20golden%20confetti%20falling%2C%20giveaway%20prize%20concept%2C%20exciting%20promotion%20atmosphere%2C%20product%20photography%20with%20vibrant%20lighting&width=800&height=500&seq=rfwev-headphones-sorteo-025&orientation=landscape',
    city: 'Online',
    state: 'Arizona',
    country: 'Estados Unidos',
    location: 'Online - App RFW',
    modality: 'online',
    capacity: 99999,
    price: 'Gratuito',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 23400, attendees: 0, interested: 18200, shared: 8900, comments: 4500 },
    score: { createEvent: 300, participate: 50, attend: 0, share: 25, invite: 35 },
    tags: ['sorteo', 'premio', 'relámpago'],
  },
  {
    id: 'evt-026',
    title: 'Festival de Cine Independiente',
    description: 'Muestra de cine independiente internacional. Proyecciones, charlas con directores y ceremonia de premiación. Organizado con Radio Internacional Voz.',
    type: 'festival',
    status: 'proximamente',
    date: '2026-08-05',
    time: '14:00 - 23:00',
    stationName: 'Radio Internacional Voz',
    stationFrequency: '140.00 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=International%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20globe%20and%20sound%20waves%2C%20cosmopolitan%20blue%20and%20gold%20tones%2C%20global%20broadcast%20brand%20identity&width=100&height=100&seq=logo-internacional&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Independent%20film%20festival%20at%20historic%20theater%2C%20movie%20screen%20with%20arthouse%20film%20screening%2C%20audience%20in%20vintage%20theater%20seats%2C%20artistic%20atmosphere%2C%20cinema%20culture%20event%20photography&width=800&height=500&seq=rfwev-film-festival-026&orientation=landscape',
    city: 'San Francisco',
    state: 'California',
    country: 'Estados Unidos',
    location: 'Castro Theatre, 429 Castro St, San Francisco, CA 94114',
    modality: 'presencial',
    capacity: 1400,
    price: '$15 USD',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 7600, attendees: 0, interested: 4100, shared: 1800, comments: 820 },
    score: { createEvent: 500, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['cultural', 'festival', 'cine'],
  },
  {
    id: 'evt-027',
    title: 'Retiro Espiritual: Renovación de Fe',
    description: 'Jornada completa de renovación espiritual con el Pastor Daniel Vega. Incluye sesiones de oración, talleres, almuerzo y transporte. Exclusivo miembros del club.',
    type: 'presencial',
    status: 'proximamente',
    date: '2026-08-22',
    time: '08:00 - 17:00',
    stationName: 'Radio Fe y Esperanza',
    stationFrequency: '87.00 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Faith%20based%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20dove%20and%20cross%20symbolism%2C%20warm%20golden%20tones%2C%20clean%20modern%20religious%20brand%20identity&width=100&height=100&seq=logo-fe-esperanza&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Spiritual%20retreat%20outdoor%20setting%20with%20peaceful%20natural%20landscape%2C%20people%20gathered%20in%20prayer%20circle%20under%20trees%2C%20serene%20garden%20atmosphere%2C%20warm%20morning%20sunlight%20filtering%20through%20leaves&width=800&height=500&seq=rfwev-retreat-fe-027&orientation=landscape',
    city: 'Miami',
    state: 'Florida',
    country: 'Estados Unidos',
    location: 'Centro de Retiros Monte Sion, 12000 SW 200th St, Miami, FL 33177',
    modality: 'presencial',
    capacity: 200,
    price: 'Miembros Club',
    clubId: 'club-002',
    clubName: 'Club Oficial Radio Fe y Esperanza',
    isClubExclusive: true,
    counters: { views: 3420, attendees: 0, interested: 890, shared: 340, comments: 210 },
    score: { createEvent: 500, participate: 25, attend: 75, share: 15, invite: 20 },
    tags: ['religioso', 'retiro', 'club', 'exclusivo'],
  },
  {
    id: 'evt-028',
    title: 'Concurso de Talento: Voces de Houston',
    description: 'El concurso de canto que reveló a los mejores talentos de la comunidad mexicana en Houston. Reviví los mejores momentos de la gran final.',
    type: 'festival',
    status: 'finalizado',
    date: '2026-05-28',
    time: '15:00 - 20:00',
    stationName: 'La Mexicana FM',
    stationFrequency: '103.50 RFW',
    stationLogoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20vibrant%20green%20red%20and%20white%20colors%2C%20traditional%20Mexican%20aesthetic%2C%20bold%20festive%20brand%20identity&width=100&height=100&seq=logo-mexicana-fm&orientation=squarish',
    imageUrl: 'https://readdy.ai/api/search-image?query=Talent%20show%20competition%20on%20theater%20stage%2C%20contestants%20performing%20with%20microphones%2C%20judges%20panel%20watching%2C%20colorful%20stage%20lighting%20and%20production%2C%20audience%20clapping%2C%20community%20entertainment%20event%20photography&width=800&height=500&seq=rfwev-talent-show-028&orientation=landscape',
    city: 'Houston',
    state: 'Texas',
    country: 'Estados Unidos',
    location: 'Auditorio Comunitario, 7800 Bissonnet St, Houston, TX 77074',
    modality: 'presencial',
    capacity: 300,
    price: 'Gratuito',
    clubId: null,
    clubName: null,
    isClubExclusive: false,
    counters: { views: 13400, attendees: 290, interested: 680, shared: 1100, comments: 670 },
    score: { createEvent: 500, participate: 25, attend: 50, share: 15, invite: 10 },
    tags: ['música', 'competencia', 'talento'],
  },
];

// ─── Helpers ────────────────────────────────────────────

export function getAllEvents(): RfwEvent[] {
  return rfwEvents;
}

export function getEventsByType(type: RfwEventType | 'todos'): RfwEvent[] {
  if (type === 'todos') return rfwEvents;
  return rfwEvents.filter((e) => e.type === type);
}

export function getEventsByStatus(status: RfwEventStatus | 'todos'): RfwEvent[] {
  if (status === 'todos') return rfwEvents;
  return rfwEvents.filter((e) => e.status === status);
}

export function getTodayEvents(): RfwEvent[] {
  return rfwEvents.filter((e) => e.status === 'hoy');
}

export function getUpcomingRfwEvents(): RfwEvent[] {
  return rfwEvents.filter((e) => e.status === 'proximamente').sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

export function getPopularEvents(): RfwEvent[] {
  return [...rfwEvents].sort((a, b) => b.counters.views - a.counters.views).slice(0, 6);
}

export function getTrendingRfwEvents(): RfwEvent[] {
  return [...rfwEvents].sort((a, b) => (b.counters.shared + b.counters.interested) - (a.counters.shared + a.counters.interested)).slice(0, 6);
}

export function getClubExclusiveEvents(): RfwEvent[] {
  return rfwEvents.filter((e) => e.isClubExclusive);
}

export function getEventById(id: string): RfwEvent | undefined {
  return rfwEvents.find((e) => e.id === id);
}

export function formatEventDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function formatEventDateShort(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
}

export function formatCounter(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

export function getDaysUntil(dateStr: string): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

export function getTimeRemainingText(dateStr: string): string {
  const days = getDaysUntil(dateStr);
  if (days < 0) return 'Finalizado';
  if (days === 0) return 'Hoy';
  if (days === 1) return 'Mañana';
  if (days <= 7) return `En ${days} días`;
  if (days <= 30) return `En ${Math.ceil(days / 7)} semanas`;
  return `En ${Math.ceil(days / 30)} meses`;
}